import React from "react";

import { Avatar, Icon } from "../components/ui";
import { linkify } from "./chatFormat";
import MessageAttachments from "./MessageAttachments";
import { kindOf } from "./attachments";

/**
 * One chat message. Own messages sit right in the accent bubble; everyone
 * else (other store admins, the RC team via Roam) sits left with avatar and
 * name. Attachments are delegated to MessageAttachments so the composer and
 * the thread agree on how each file type looks.
 *
 * A bubble containing nothing but images loses its padding and background —
 * a photo inside a tinted, padded box reads as a thumbnail in a card, where
 * every chat app in the world shows it as the message itself.
 */

const timeOf = (iso) =>
  new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

/**
 * Delivery state, shown only on your own messages — nobody needs a tick on
 * someone else's. `failed` is handled separately below as an action, not a
 * glyph, because it is the one state the user can do something about.
 */
const StatusMark = ({ status }) => {
  if (status === "pending") return <Icon name="Clock" size="xs" label="Sending" />;
  if (status === "sent") return <Icon name="Check" size="xs" label="Sent" />;

  return null;
};

const MessageBubble = ({ message, showAuthor = true, onRetry, onOpenImage }) => {
  const self = Boolean(message.author?.is_self);
  const deleted = message.content_type === "deleted";
  const failed = message.status === "failed";
  const pending = message.status === "pending";
  const attachments = message.attachments || [];

  // Images-only: no body text, and nothing that renders as a file card.
  const mediaOnly =
    !message.body &&
    attachments.length > 0 &&
    attachments.every((attachment) => kindOf(attachment) === "image");

  return (
    <div
      className={[
        "rc-chat__row",
        self ? "rc-chat__row--self" : "rc-chat__row--other",
        showAuthor ? "rc-chat__row--lead" : "rc-chat__row--follow",
      ].join(" ")}
    >
      {!self && (
        <div className="rc-chat__gutter">
          {showAuthor ? (
            <Avatar
              name={message.author?.name}
              src={message.author?.image_url || undefined}
              size="sm"
            />
          ) : null}
        </div>
      )}

      <div className="rc-chat__stack">
        {!self && showAuthor && (
          <div className="rc-chat__author">
            {message.author?.name}
            {message.author?.is_rc && (
              <span className="rc-chat__author-tag">Retail Circle</span>
            )}
          </div>
        )}

        <div
          className={[
            "rc-chat__bubble",
            self ? "rc-chat__bubble--self" : "",
            deleted ? "rc-chat__bubble--deleted" : "",
            pending ? "rc-chat__bubble--pending" : "",
            failed ? "rc-chat__bubble--failed" : "",
            mediaOnly ? "rc-chat__bubble--media" : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {deleted ? (
            <span className="rc-chat__deleted">
              <Icon name="Trash2" size="xs" /> Message deleted
            </span>
          ) : (
            <>
              <MessageAttachments attachments={attachments} onOpenImage={onOpenImage} />
              {message.body ? (
                <div className="rc-chat__text">{linkify(message.body)}</div>
              ) : null}
            </>
          )}

          {!deleted && (
            <div className="rc-chat__meta">
              <span>{timeOf(message.created_at)}</span>
              {self && <StatusMark status={message.status} />}
            </div>
          )}
        </div>

        {failed && (
          <button
            type="button"
            className="rc-chat__retry"
            onClick={() => onRetry?.(message)}
          >
            <Icon name="RefreshCw" size="xs" /> Not delivered — tap to retry
          </button>
        )}
      </div>
    </div>
  );
};

export default MessageBubble;
